import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Phone, 
  Mail, 
  MapPin, 
  Send,
  CheckCircle2,
  Facebook,
  Instagram
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const serviceOptions = [
  "Welding & Fabrication",
  "Mobile Onsite Welding",
  "Custom Attachments & Loader Buckets",
  "Livestock Infrastructure",
  "CAD Design & Custom Projects",
  "General Enquiry"
];

export default function ContactPage() {
  const [searchParams] = useSearchParams();
  const productFromUrl = searchParams.get('product');

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    product: productFromUrl || "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    if (productFromUrl) {
      setFormData(prev => ({
        ...prev,
        product: productFromUrl,
        message: `I'm interested in getting a quote for: ${productFromUrl}\n\nPlease provide more information about:`
      }));
    }
  }, [productFromUrl]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (value) => {
    setFormData(prev => ({ ...prev, service: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      toast.success("Message sent successfully! We'll be in touch soon.");
      setFormData({
        name: "",
        email: "",
        phone: "",
        service: "",
        product: "",
        message: ""
      });
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Failed to send message. Please try again or call us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="page-transition pt-20" data-testid="contact-page">
      <Helmet>
        <title>Contact PWC Fabrication and Engineering | Get a Quote Albany & Great Southern WA</title>
        <meta name="description" content="Contact PWC Fabrication and Engineering for a free quote on horse shelters, stick rakes, custom fabrication & mobile welding in Albany & Great Southern WA. Call 0402 275 003." />
        <meta name="keywords" content="contact PWC Fabrication, get a quote Albany, fabrication quote Great Southern, horse shelter quote WA, stick rake quote, mobile welding contact Albany" />
        <link rel="canonical" href="https://pwcengineering.com.au/contact" />
      </Helmet>
      
      {/* Hero Section */}
      <section className="py-20 md:py-32 bg-[#121212]" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-block text-[#E63946] font-semibold uppercase tracking-widest mb-4 text-sm">
              Get in Touch
            </span>
            <h1 className="text-5xl md:text-6xl font-bold tracking-tight uppercase text-white mb-6">
              Contact Us
            </h1>
            <p className="text-xl text-[#A3A3A3] leading-relaxed">
              Ready to start your project? Have a question? Get in touch with us 
              and we'll get back to you as soon as possible.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 md:py-32 bg-[#0A0A0A]" data-testid="contact-form-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <div className="order-2 lg:order-1">
              <h2 className="text-3xl font-bold uppercase tracking-wide text-white mb-8">
                Let's Talk
              </h2>
              <p className="text-lg text-[#A3A3A3] leading-relaxed mb-8">
                Whether you need a quote, have questions about our services, or want 
                to discuss a custom project, we're here to help. Give us a call or 
                fill out the form and we'll get back to you.
              </p>

              {/* Contact Details */}
              <div className="space-y-4 mb-12">
                <a 
                  href="tel:0402275003" 
                  className="flex items-center gap-4 p-5 bg-[#121212] border border-[#2A2A2A] rounded-2xl hover:border-[#E63946] transition-colors group"
                  data-testid="contact-phone"
                >
                  <div className="w-14 h-14 bg-[#E63946] rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="text-white" size={24} />
                  </div>
                  <div>
                    <p className="text-[#525252] text-sm uppercase tracking-wider">Phone</p>
                    <p className="text-white text-xl font-bold group-hover:text-[#E63946] transition-colors">
                      0402 275 003
                    </p>
                  </div>
                </a>

                <a 
                  href="mailto:phillip.pwc@gmail.com" 
                  className="flex items-center gap-4 p-5 bg-[#121212] border border-[#2A2A2A] rounded-2xl hover:border-[#E63946] transition-colors group"
                  data-testid="contact-email"
                >
                  <div className="w-14 h-14 bg-[#E63946] rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="text-white" size={24} />
                  </div>
                  <div>
                    <p className="text-[#525252] text-sm uppercase tracking-wider">Email</p>
                    <p className="text-white text-lg font-bold group-hover:text-[#E63946] transition-colors">
                      phillip.pwc@gmail.com
                    </p>
                  </div>
                </a>

                <div className="flex items-center gap-4 p-5 bg-[#121212] border border-[#2A2A2A] rounded-2xl">
                  <div className="w-14 h-14 bg-[#E63946] rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-white" size={24} />
                  </div>
                  <div>
                    <p className="text-[#525252] text-sm uppercase tracking-wider">Service Area</p>
                    <p className="text-white text-lg font-bold">
                      Great Southern, Western Australia
                    </p>
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div>
                <p className="text-white font-bold uppercase tracking-wider mb-4">
                  Follow Us
                </p>
                <div className="flex flex-col gap-3">
                  <a 
                    href="https://www.facebook.com/61574081204175/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-3 p-4 bg-[#121212] border border-[#2A2A2A] rounded-xl hover:border-[#E63946] transition-colors"
                    data-testid="contact-facebook"
                  >
                    <Facebook className="text-[#E63946]" size={24} />
                    <span className="text-white font-semibold">Facebook</span>
                  </a>
                  <a 
                    href="https://www.instagram.com/pwc_fab_engineering?utm_source=qr&igsh=MXJoNWV0ODB4cXZzMA==" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-3 p-4 bg-[#121212] border border-[#2A2A2A] rounded-xl hover:border-[#E63946] transition-colors"
                    data-testid="contact-instagram"
                  >
                    <Instagram className="text-[#E63946]" size={24} />
                    <span className="text-white font-semibold">Instagram</span>
                  </a>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-[#121212] border border-[#2A2A2A] rounded-3xl p-8 order-1 lg:order-2">
              {isSubmitted ? (
                <div className="text-center py-12" data-testid="success-message">
                  <div className="w-20 h-20 bg-[#E63946]/10 border border-[#E63946]/30 rounded-full mx-auto mb-6 flex items-center justify-center">
                    <CheckCircle2 className="text-[#E63946]" size={40} />
                  </div>
                  <h3 className="text-2xl font-bold uppercase tracking-wide text-white mb-4">
                    Message Sent!
                  </h3>
                  <p className="text-[#A3A3A3] mb-8">
                    Thanks for getting in touch. We'll review your message and get back 
                    to you as soon as possible.
                  </p>
                  <Button 
                    onClick={() => setIsSubmitted(false)}
                    className="bg-[#E63946] hover:bg-[#D62839] text-white font-bold uppercase tracking-wider px-6 py-3 rounded-full"
                    data-testid="send-another-btn"
                  >
                    Send Another Message
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-testid="contact-form">
                  <h3 className="text-2xl font-bold uppercase tracking-wide text-white mb-6">
                    Request a Quote
                  </h3>

                  <div className="space-y-6">
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="name" className="text-white mb-2 block">
                          Name <span className="text-[#E63946]">*</span>
                        </Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="Your name"
                          className="bg-[#0A0A0A] border-[#2A2A2A] focus:border-[#E63946] text-white placeholder:text-gray-600 rounded-xl"
                          required
                          data-testid="input-name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="phone" className="text-white mb-2 block">
                          Phone
                        </Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="Your phone number"
                          className="bg-[#0A0A0A] border-[#2A2A2A] focus:border-[#E63946] text-white placeholder:text-gray-600 rounded-xl"
                          data-testid="input-phone"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="email" className="text-white mb-2 block">
                        Email <span className="text-[#E63946]">*</span>
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="your@email.com"
                        className="bg-[#0A0A0A] border-[#2A2A2A] focus:border-[#E63946] text-white placeholder:text-gray-600 rounded-xl"
                        required
                        data-testid="input-email"
                      />
                    </div>

                    <div>
                      <Label htmlFor="service" className="text-white mb-2 block">
                        Service Required
                      </Label>
                      <Select value={formData.service} onValueChange={handleServiceChange}>
                        <SelectTrigger 
                          className="bg-[#0A0A0A] border-[#2A2A2A] focus:border-[#E63946] text-white rounded-xl"
                          data-testid="select-service"
                        >
                          <SelectValue placeholder="Select a service" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#121212] border-[#2A2A2A]">
                          {serviceOptions.map((service) => (
                            <SelectItem 
                              key={service} 
                              value={service}
                              className="text-white hover:bg-[#2A2A2A] focus:bg-[#2A2A2A]"
                            >
                              {service}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {formData.product && (
                      <div>
                        <Label htmlFor="product" className="text-white mb-2 block">
                          Product Interest
                        </Label>
                        <Input
                          id="product"
                          name="product"
                          value={formData.product}
                          onChange={handleChange}
                          placeholder="Product you're interested in"
                          className="bg-[#0A0A0A] border-[#2A2A2A] focus:border-[#E63946] text-white placeholder:text-gray-600 rounded-xl"
                          data-testid="input-product"
                        />
                      </div>
                    )}

                    <div>
                      <Label htmlFor="message" className="text-white mb-2 block">
                        Message <span className="text-[#E63946]">*</span>
                      </Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Tell us about your project or enquiry..."
                        rows={6}
                        className="bg-[#0A0A0A] border-[#2A2A2A] focus:border-[#E63946] text-white placeholder:text-gray-600 rounded-xl resize-none"
                        required
                        data-testid="input-message"
                      />
                    </div>

                    <Button 
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-[#E63946] hover:bg-[#D62839] text-white font-bold uppercase tracking-wider py-4 rounded-full btn-glow disabled:opacity-50"
                      data-testid="submit-btn"
                    >
                      {isSubmitting ? (
                        "Sending..."
                      ) : (
                        <>
                          Send Message
                          <Send className="ml-2" size={18} />
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Map/Service Area */}
      <section className="py-20 md:py-32 bg-[#121212]" data-testid="service-area-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-[#E63946] font-semibold uppercase tracking-widest mb-4 text-sm">
            Service Area
          </p>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight uppercase text-white mb-6">
            Great Southern Region
          </h2>
          <p className="text-xl text-[#A3A3A3] max-w-2xl mx-auto mb-8">
            We service the Great Southern region of Western Australia, including Albany, 
            Katanning, Mount Barker, Denmark, and surrounding areas. Our services are 
            available across the region.
          </p>
          <div className="inline-flex items-center gap-4 p-6 bg-[#1A1A1A] border border-[#2A2A2A] rounded-sm">
            <MapPin className="text-[#E63946]" size={32} />
            <span className="text-white text-xl font-bold">Great Southern, WA</span>
          </div>
        </div>
      </section>
    </div>
  );
}
